import React from 'react';
import { AdminUserList } from './AdminUserList';
import { AirportsScreen } from './AirportsScreen';
import { OpportunitiesScreen } from './OpportunitiesScreen';
import { RegistrationsList } from './RegistrationsList';
import { SalesForm } from './SalesForm';
import { SalesList } from './SalesList';
import { WithdrawalForm } from './WithdrawalForm';
import { WithdrawalsList } from './WithdrawalsList';
import { Users, Plane, Tag, Menu, X, DollarSign, Wallet, LogOut, MapPin } from 'lucide-react';
import { signOut } from '../lib/auth';

export function AdminScreen() {
  const [activeTab, setActiveTab] = React.useState('users');
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showSalesForm, setShowSalesForm] = React.useState(true);
  const [showWithdrawalForm, setShowWithdrawalForm] = React.useState(true);

  // Close menu when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const menu = document.getElementById('admin-menu');
      const menuButton = document.getElementById('menu-button');
      
      if (menu && !menu.contains(event.target as Node) && 
          menuButton && !menuButton.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      window.location.reload();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-md z-50">
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            id="menu-button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu size={24} className="text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <img 
              src="/assets/logo/icone_logo_ft.png"
              alt="FT Viagens"
              className="h-8 w-8 object-contain"
            />
            <h1 className="text-lg font-semibold text-gray-900">
              Painel Admin
            </h1>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 text-gray-600 hover:text-red-600 transition-colors rounded-lg"
            title="Sair"
          >
            <LogOut size={24} />
          </button>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden lg:flex fixed top-0 left-0 right-0 bg-white shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 w-full">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/assets/logo/icone_logo_ft.png"
                alt="FT Viagens"
                className="h-10 w-10 object-contain"
              />
              <h1 className="text-xl font-semibold text-gray-900">
                Painel Administrativo
              </h1>
            </div>

            <div className="flex items-center gap-4">
              <nav className="flex space-x-4">
                <button
                  onClick={() => handleTabChange('users')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'users'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Users size={20} />
                  <span>Usuários</span>
                </button>
                <button
                  onClick={() => handleTabChange('registrations')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'registrations'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <MapPin size={20} />
                  <span>Alertas</span>
                </button>
                <button
                  onClick={() => handleTabChange('airports')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'airports'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Plane size={20} />
                  <span>Aeroportos</span>
                </button>
                <button
                  onClick={() => handleTabChange('opportunities')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'opportunities'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Tag size={20} />
                  <span>Oportunidades</span>
                </button>
                <button
                  onClick={() => handleTabChange('sales')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'sales'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <DollarSign size={20} />
                  <span>Vendas</span>
                </button>
                <button
                  onClick={() => handleTabChange('withdrawals')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === 'withdrawals'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Wallet size={20} />
                  <span>Saques</span>
                </button>
              </nav>

              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-red-600 transition-colors rounded-lg"
                title="Sair"
              >
                <LogOut size={20} />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" />
      )}

      {/* Mobile Menu */}
      <div
        id="admin-menu"
        className={`lg:hidden fixed top-0 left-0 bottom-0 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <img 
              src="/assets/logo/icone_logo_ft.png"
              alt="FT Viagens"
              className="h-8 w-8 object-contain"
            />
            <h2 className="font-semibold text-gray-900">Menu</h2>
          </div>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <nav className="p-4">
          <button
            onClick={() => handleTabChange('users')}
            className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-2 ${
              activeTab === 'users'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Users size={20} />
            <span>Usuários</span>
          </button>
          <button
            onClick={() => handleTabChange('registrations')}
            className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-2 ${
              activeTab === 'registrations'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <MapPin size={20} />
            <span>Alertas</span>
          </button>
          <button
            onClick={() => handleTabChange('airports')}
            className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-2 ${
              activeTab === 'airports'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Plane size={20} />
            <span>Aeroportos</span>
          </button>
          <button
            onClick={() => handleTabChange('opportunities')}
            className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-2 ${
              activeTab === 'opportunities'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Tag size={20} />
            <span>Oportunidades</span>
          </button>
          <button
            onClick={() => handleTabChange('sales')}
            className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-2 ${
              activeTab === 'sales'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <DollarSign size={20} />
            <span>Vendas</span>
          </button>
          <button
            onClick={() => handleTabChange('withdrawals')}
            className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'withdrawals'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Wallet size={20} />
            <span>Saques</span>
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <main className="pt-16 lg:pt-20 px-4 max-w-7xl mx-auto">
        <div className="py-6">
          {activeTab === 'users' && <AdminUserList />}
          {activeTab === 'registrations' && <RegistrationsList />}
          {activeTab === 'airports' && <AirportsScreen />}
          {activeTab === 'opportunities' && <OpportunitiesScreen />}
          {activeTab === 'sales' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  onClick={() => setShowSalesForm(true)}
                  className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
                    showSalesForm
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Nova Venda
                </button>
                <button
                  onClick={() => setShowSalesForm(false)}
                  className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
                    !showSalesForm
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Lista de Vendas
                </button>
              </div>

              {showSalesForm ? (
                <SalesForm onSuccess={() => setShowSalesForm(false)} />
              ) : (
                <SalesList />
              )}
            </div>
          )}
          {activeTab === 'withdrawals' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  onClick={() => setShowWithdrawalForm(true)}
                  className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
                    showWithdrawalForm
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Novo Saque
                </button>
                <button
                  onClick={() => setShowWithdrawalForm(false)}
                  className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
                    !showWithdrawalForm
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Lista de Saques
                </button>
              </div>

              {showWithdrawalForm ? (
                <WithdrawalForm onSuccess={() => setShowWithdrawalForm(false)} />
              ) : (
                <WithdrawalsList />
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}